import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';

import { isExternalUrl } from '../../utils/url';
import { serializeImage } from '../../utils/SerializeImage';
import BackgroundImage from '../BackgroundImage';
import ImageCaption from '../ImageCaption';
import AnchorLink from '../AnchorLink';
import RichText from '../RichText';
import Button from '../Button';

import styles from './PostSegment.module.scss';

const PostSegment = ({
    items = [
        {
            value: {},
        },
    ],
    isNumberedList = false,
    altLayout = '',
    withTypeStyle,
    untranslatedModifiers = [],
    hasParentTitle = false,
    colors = '',
}) => {
    const classes = classNames(
        styles['PostSegment'],
        {
            [styles['PostSegment--Numbered']]: isNumberedList,
            [styles[`PostSegment--${withTypeStyle}`]]: withTypeStyle,
        },
        untranslatedModifiers.map((x) => styles[x])
    );

    return (
        <div className={classes}>
            {items.map((item, index) => (
                <Card
                    colors={colors}
                    {...item}
                    key={index}
                    index={index}
                    numbered={isNumberedList}
                    withTypeStyle={withTypeStyle}
                    altLayout={altLayout}
                    hTag={hasParentTitle ? 'h3' : 'h2'}
                />
            ))}
        </div>
    );
};

PostSegment.propTypes = {
    items: PropTypes.array,
    isNumberedList: PropTypes.bool,
    altLayout: PropTypes.string,
    withTypeStyle: PropTypes.string,
    hasParentTitle: PropTypes.bool,
};

const Card = ({
    href = '',
    image = {
        src: '',
        caption: '',
    },
    title = '',
    anchorLink = '',
    text = '',
    numbered = false,
    buttonText,
    index,
    altLayout,
    withTypeStyle,
    colors,
    hTag = 'h2',
}) => {
    const { t } = useTranslation('common');
    const serializedImage = serializeImage(image, true);
    const hasLink = href.length > 0;
    const isExternal = hasLink && isExternalUrl(href);
    const classes = classNames(styles['PostSegment__Card'], {
        [styles[`PostSegment__Card--${colors}`]]: colors,
    });

    const sizes =
        '(min-width: 1440px) 642px, (min-width: 1280px) 560px, (min-width: 1024px) 920px, (min-width: 768px) 720px, 100vw';

    let useLeftAlign = false;

    if (altLayout === 'ImageLeftStart') {
        useLeftAlign = Boolean(index % 2);
    } else if (altLayout === 'ImageRightStart') {
        useLeftAlign = !Boolean(index % 2);
    }

    const Heading = hTag;

    const containerClasses = classNames(styles['PostSegment__Container'], {
        [styles['PostSegment__Container--Link']]: hasLink,
        [styles['PostSegment__Container--Numbered']]: numbered,
        [styles['PostSegment__Container--External']]: isExternal,
        [styles['PostSegment__Container--LeftAlign']]: useLeftAlign,
        [styles['PostSegment__Container--RightAlign']]: !useLeftAlign,
        [styles[`PostSegment__Container--${withTypeStyle}`]]: withTypeStyle,
    });
    const showCaption = image.title || image.credits;

    return (
        <div className={classes}>
            <div className={containerClasses}>
                <div className={styles['PostSegment__Image']}>
                    {serializedImage && <BackgroundImage {...serializedImage} quality={70} sizes={sizes} />}
                    {showCaption && (
                        <ImageCaption
                            id={`image-${image.id}`}
                            credits={image.credits}
                            caption={image.caption}
                            title={image.title}
                        />
                    )}
                </div>

                <div className={styles['PostSegment__Wrapper']}>
                    <div className={styles['PostSegment__Content']}>
                        <Heading className={styles['PostSegment__Title']}>
                            {numbered && (
                                <span aria-hidden={true} className={styles['PostSegment__Number']}>
                                    {index + 1}
                                </span>
                            )}
                            <AnchorLink title={title} id={anchorLink} />
                        </Heading>
                        <div className={styles['PostSegment__Text']}>
                            <RichText html={text} colors={colors} />
                            {hasLink && (
                                <Button type="Secondary" href={href} className={styles['PostSegment__ReadMore']}>
                                    <span className="sr-only">{title}</span>
                                    <span aria-hidden={true}>{buttonText || t('postsegment.more')}</span>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

Card.propTypes = {
    href: PropTypes.string,
    image: PropTypes.object,
    title: PropTypes.string,
    anchorLink: PropTypes.string,
    text: PropTypes.string,
    single: PropTypes.bool,
    numbered: PropTypes.bool,
    buttonText: PropTypes.string,
    index: PropTypes.number,
};

export default PostSegment;
